import { useCallback, useEffect, useState } from 'react';

export enum UpdateStatus {
  checking = 'checking',
  current = 'current',
  available = 'available',
}

/**
 * The current version of the app based on latest git SHA of the commit
 */
const currentVersion = process.env.VERCEL_GIT_COMMIT_SHA
  ? process.env.VERCEL_GIT_COMMIT_SHA.slice(0, 7)
  : 'local';

/**
 * Fetches the app version from the server.
 * @returns The app version.
 */
async function getAppVersion() {
  try {
    const data = await fetch(`/version.json`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-store',
      },
    });
    const json = await data.json();
    return json.buildNumber;
  } catch (err) {
    console.error('Failed to fetch app version', { err });
    return currentVersion;
  }
}

/**
 * Check if a new version of the app is available.
 * @returns Whether a new version is available.
 */
export function useIsUpdateAvailable() {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  useEffect(() => {
    (async () => {
      const version = await getAppVersion();
      setIsUpdateAvailable(version !== currentVersion);
    })();
  }, []);

  return isUpdateAvailable;
}

/**
 * Reload the page.
 */
const reloadPage = () => window.location.reload();

/**
 * Check for a new version of the app on the server.
 * @param interval - The interval in milliseconds to check for a new version.
 */
export function useVersionCheck({ interval }: { interval: number }) {
  const [status, setStatus] = useState<UpdateStatus>(UpdateStatus.checking);

  const checkUpdate = useCallback(() => {
    (async () => {
      setStatus(UpdateStatus.checking);

      const version = await getAppVersion();
      setStatus(
        version !== currentVersion
          ? UpdateStatus.available
          : UpdateStatus.current,
      );
    })();
  }, []);

  useEffect(() => {
    checkUpdate();
  }, [checkUpdate]);

  useEffect(() => {
    if (status !== UpdateStatus.current) return;

    const timeoutId = setTimeout(() => checkUpdate(), interval);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [interval, status, checkUpdate]);

  return { status, reloadPage };
}
